import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  subscriptionUsageAtom,
  subscriptionLoadingAtom,
  creditsDisplayAtom,
  creditsIncludedAtom,
  creditsUsedAtom,
  userAtom,
} from '../state/authAtoms';
import { getCurrentSubscriptionUsage } from '../lib/subscriptions';
import type { SubscriptionUsageWithDetails } from '../lib/subscriptions';
import { logger } from '../lib/logger';

interface UseCreditsReturn {
  /** Current credits remaining (from subscription or profile fallback) */
  credits: number;
  /** Total credits included in the current billing period */
  creditsIncluded: number;
  /** Credits used this billing period */
  creditsUsed: number;
  /** Full subscription usage data (null if no active subscription) */
  subscriptionUsage: SubscriptionUsageWithDetails | null;
  /** Whether credits are currently being loaded */
  isLoading: boolean;
  /** Refresh credits from the server */
  refreshCredits: () => Promise<void>;
  /** Check if user has enough credits for an operation */
  hasCredits: (required: number) => boolean;
}

/**
 * Hook for accessing and managing user credits throughout the app.
 * Provides consistent access to credits state via Jotai atoms.
 * 
 * @example
 * ```tsx
 * const { credits, isLoading, refreshCredits, hasCredits } = useCredits();
 * 
 * // Check before staging
 * if (!hasCredits(1)) {
 *   showUpgradeModal();
 *   return;
 * }
 * 
 * // After staging, refresh credits
 * await stageImage();
 * await refreshCredits();
 * ```
 */
export function useCredits(): UseCreditsReturn {
  const user = useAtomValue(userAtom);
  const credits = useAtomValue(creditsDisplayAtom);
  const creditsIncluded = useAtomValue(creditsIncludedAtom);
  const creditsUsed = useAtomValue(creditsUsedAtom);
  const [subscriptionUsage, setSubscriptionUsage] = useAtom(subscriptionUsageAtom);
  const [isLoading, setIsLoading] = useAtom(subscriptionLoadingAtom);

  const refreshCredits = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const usage = await getCurrentSubscriptionUsage(user.id);
      setSubscriptionUsage(usage);
    } catch (error) {
      logger.error('Error refreshing credits:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, setSubscriptionUsage, setIsLoading]);

  const hasCredits = useCallback(
    (required: number) => {
      return credits >= required;
    },
    [credits]
  );

  return {
    credits,
    creditsIncluded,
    creditsUsed,
    subscriptionUsage,
    isLoading,
    refreshCredits,
    hasCredits,
  };
}

/**
 * Lightweight hook for components that only need to display credits.
 * Does not include refresh functionality for better performance.
 */
export function useCreditsDisplay(): number {
  return useAtomValue(creditsDisplayAtom);
}

/**
 * Hook for components that need to update credits state.
 * Returns the setter function for subscriptionUsageAtom.
 */
export function useSetCredits() {
  return useSetAtom(subscriptionUsageAtom);
}

